// Libraries
import React from 'react';
import { connect } from 'react-redux';
import { Map } from 'immutable';
import { Modal } from 'react-bootstrap';
import { defineMessages, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
// Local code base
import './DotInspectionContainer.css';
import DotInspectionResultConfirmation from '../components/DotInspectionResultConfirmation';
import DotInspectionResultConfirmationFailOptions from '../components/DotInspectionResultConfirmationFailOptions';
import DotInspectionForm from '../components/DotInspectionForm';
import DotInspectionFormFailOptions from '../components/DotInspectionFormFailOptions';
import * as constants from 'constants/App';
import * as selectors from '../selectors/DotInspectionSelector';
import FormWrapper from 'components/FormWrapper';
import * as dotInspectionEnums from '../data/DotInspectionEnums';

/// Summary
/// DOT Inspection is set up as a 2 step proccess. This is to make it easy for the vendor.
/// First vendor will mark if DOT inspection passed or failed.
/// If Pass, they attest and "sign" their name and save. Done.
/// If Fail, they are asked if they will repair failed components:
///     -Yes: vendor redirected to Edit PO.
///     -No:  take to DOT Inspection form to specify failed components.
///
class DotInspectionContainer extends React.Component {
    constructor(props) {
        super(props);
        
        this.state = {
            step: this.step.result_confirmation
        };
    }

	step = {
		result_confirmation: 'result_confirmation',
		result_confirmation_fail_options: 'result_confirmation_fail_options',
		form: 'form',
		form_fail_options: 'form_fail_options',
	}

    componentDidMount() {
		this.props.reset();
	}
	
	componentDidUpdate(prevProps, prevState) {		
		if (prevProps.showWindow === false && this.props.showWindow === true) {			
			this.props.setDefaultValues();		
			this.setState({ step: this.step.result_confirmation }); // reset to first step
		}
	}

	/* Close window */
	handleCloseWindowClick = () => {
		this.props.cancelClosePo();
		this.props.closeWindow();
	}

	handleDotFormClick = () => {
		this.props.setFormData('inspectorName', this.props.formData.get('inspectorSignature'));
		this.props.resetError();
		this.props.resetValidationMessages();
        this.setState({ step: this.step.form });
	} 
	
	handleSaveConfirmationClick = () => {
		if (this.props.isSaving) {
			return;
		}
		
		const isValid = this.validateConfirmation();
		
        if (isValid === true) {
			this.props.saveConfirmation();			
		}
	}

	validateConfirmation = ()=> {
        let isValid = true; // Start off valid.
        this.props.resetValidationMessages();

        if (!(this.props.formData.get('hasInspectionPassed') === true 
            || this.props.formData.get('hasInspectionPassed') === false)) 
        {
            this.props.setValidationMessage('hasInspectionPassed', 'dot_inspection__has_inspection_passed_required')
            isValid = false;
        }

		//Inspection Date validation.
		if (this.props.formData.get('inspectionDate') === null)
		{
			this.props.setValidationMessage('inspectionDate', 'dot_inspection__inspection_date_disabled');
			isValid = false;
		}

        if (this.props.formData.get('inspectorAttested') !== true) 
        {
            this.props.setValidationMessage('inspectorAttested', 'dot_inspection__attest_required')
            isValid = false;
        }

        if (this.props.formData.get('inspectorSignature') === undefined
            || this.props.formData.get('inspectorSignature') === '') 
        {
            this.props.setValidationMessage('inspectorSignature', 'dot_inspection__signature_required')
            isValid = false;
        }

        
        return isValid;        
    }
	
	onInspectionDateChangeHandler = (isDaySelected,isDisabled) =>{
		if (isDisabled){
			this.props.setValidationMessage('inspectionDate', 'dot_inspection__inspection_date_disabled')
		} else if (!isDaySelected)
		{
			this.props.setValidationMessage('inspectionDate', 'dot_inspection__inspection_date_invalid');
		}
		else{
			this.props.resetValidationMessages();
		}
	}

	handleConfirmationFailClick = () => {
		this.props.resetError();
		this.props.resetValidationMessages();
		this.setState({ step: this.step.result_confirmation_fail_options });
	}

	handleConfirmationFailYesClick = () => {
		this.props.closeWindow();
		// redirect to edit PO
		this.props.navigateToEditPo(this.props.poNumber);
	}

	handleConfirmationFailNoClick = () => {
		this.props.skipFailOptions();
		this.props.resetError();
		this.setState({ step: this.step.form });
	}           

	handleAllOkClick = (name, checked) => {
		if (checked) {
			this.props.setAllVehicleComponentsOk();
		}
	}

	handleFormSaveClick = () => {		
		if (this.props.isSaving) {
			return;
		}
		
		const isValid = this.validateForm();

        if (isValid) {					
			if (this.props.formData.get('hasInspectionPassed') === true) {
				this.props.saveForm();				
			}
			else if (this.props.formData.get('hasInspectionPassed') === false) {
				if (this.props.shouldSkipFailOptions === true) {
					this.props.saveForm();					
				}
				else {
					this.props.resetError();
					this.setState({ step: this.step.form_fail_options });
				}
			}
			else {
				// DO NOTHING - validation should prevent hasInspectionPassed from being null.
			}
		}
	}

	validateForm = ()=> {
        let isValid = true; // Start off valid.
        this.props.resetValidationMessages();

		// Inspector name required.
		if ((this.props.formData.get('inspectorName') || '') === '') 
        {
            this.props.setValidationMessage('inspectorName', 'dot_inspection__inspector_name_required');
            isValid = false;
		}

		// PASSED inspection: inspection qualified required
		if (this.props.formData.get('hasInspectionPassed') === true
			&& this.props.formData.get('inspectorQualified') !== true) 
        {
            this.props.setValidationMessage('inspectorQualified', 'dot_inspection__inspector_qualified_required');
            isValid = false;
		}

		// PASS or FAIL required.
		if (!(this.props.formData.get('hasInspectionPassed') === true 
            || this.props.formData.get('hasInspectionPassed') === false)) 
        {
            this.props.setValidationMessage('hasInspectionPassed', 'dot_inspection__has_inspection_passed_required');
            isValid = false;
        }        
			
		// PASSED inspection: all components must be passed OR NA have a repaired date.
		if (this.props.formData.get('hasInspectionPassed') === true
			&& this.props.componentDetails.count((comp) => 
				comp.get('componentResultCode') === dotInspectionEnums.componentResultCode.PASS
				|| comp.get('componentResultCode') === dotInspectionEnums.componentResultCode.NA
				|| comp.get('repairedDate') !== null)  
				< this.props.componentDetails.count())
        {
            this.props.setValidationMessage('hasInspectionPassed', 'dot_inspection__has_inspection_passed_components_invalid');
            isValid = false;
		}

		// PASSED inspection: other failure conditions needs to be blank.
		if (this.props.formData.get('hasInspectionPassed') === true
			&& (this.props.formData.get('otherFailureReasons') || '') !== '' )
        {
            this.props.setValidationMessage('hasInspectionPassed', 'dot_inspection__has_inspection_passed_unsafe_conditions');
            isValid = false;
		}

		// FAILED inspection:
		if (this.props.formData.get('hasInspectionPassed') === false) {
			// FAILED inspection: All components must specify OK/NA/Need Repair.
			if (this.props.componentDetails.count((comp) =>
					comp.get('componentResultCode') === dotInspectionEnums.componentResultCode.PASS
					|| comp.get('componentResultCode') === dotInspectionEnums.componentResultCode.NA
					|| comp.get('componentResultCode') === dotInspectionEnums.componentResultCode.FAIL) < this.props.componentDetails.count()) {
				this.props.setValidationMessage('hasInspectionPassed', 'dot_inspection__has_inspection_failed_all_components_required');
				isValid = false;
			}
			// FAILED inspection: at least one component must be failed OR other failure conditions filled out.
			else if ((this.props.componentDetails.count((comp) =>
					comp.get('componentResultCode') === dotInspectionEnums.componentResultCode.FAIL 
					&& comp.get('repairedDate') === null) === 0)
					&& (this.props.formData.get('otherFailureReasons') || '') === '')
			{
				this.props.setValidationMessage('hasInspectionPassed', 'dot_inspection__has_inspection_passed_no_failure_specified');
				isValid = false;
			}			
		}

		//Inspection Date validation.
		if (this.props.formData.get('inspectionDate') === null)
		{
			this.props.setValidationMessage('inspectionDate', 'dot_inspection__inspection_date_disabled');
			isValid = false;
		}

		// PASSED inspection: ATTEST required.
		if (this.props.formData.get('hasInspectionPassed') === true 
			&& this.props.formData.get('inspectorAttested') !== true) 
        {
            this.props.setValidationMessage('inspectorAttested', 'dot_inspection__attest_required');
            isValid = false;
        }

		// inspection: SIGNATURE required.
		if (this.props.formData.get('inspectorSignature') === undefined
				|| (this.props.formData.get('inspectorSignature') || '') === ''
			)
        {
            this.props.setValidationMessage('inspectorSignature', 'dot_inspection__signature_required');
            isValid = false;
		}

        return isValid;        
	}

	handleFormFailYesClick = () => {
		this.props.closeWindow();
		// redirect to edit PO
		this.props.navigateToEditPo(this.props.poNumber);
	}

	handleFormFailNoClick = () => {		
		if (this.props.isSaving) {
			return;
		}
			
		this.props.saveForm();				
	}

    render() {
        const messages = defineMessages({
            close_dialog: {
                id: 'close_dialog',
                description: '  Close',
                defaultMessage: 'Close'
            },              
        })
                
        return (
            <div>
				<Modal 
					show={this.props.showWindow}                
					dialogClassName={classnames('dot_inspection',
						{'dot_inspection--small': 
							this.state.step === this.step.form_fail_options
							|| this.state.step === this.step.result_confirmation_fail_options
						})}
                    backdrop="static"
                    keyboard={false}
                    onEntered={() => this.setState({ modalLoaded: true })}
                >
					<Modal.Body>
						<div 
							className='dot_inspection__body'>

							{(this.state.step === this.step.form || this.state.step === this.step.result_confirmation) &&
								<div onClick={this.handleCloseWindowClick} className='dot_inspection__close_button'>
									<img className="add-new-line-items__close-button"
										src="/assets/VMS_33button_kill_blue.png" />{"  "}
									{<FormattedMessage {...messages.close_dialog} />}
								</div>
							}

							<FormWrapper key='dotInspectionErrorMessage' id="error_message" {...this.props} formErrors={this.props.dotInspectionErrorMessage || this.props.errorMessage} />    

                            {this.state.step === this.step.result_confirmation && 
								<DotInspectionResultConfirmation 
									formData={this.props.formData}
                                    formValidationMessages={this.props.formValidationMessages}
									setFormData={this.props.setFormData}
									isSaving={this.props.isSaving}
									userLocale={this.props.userLocale}
									poCreationDate={this.props.poCreationDate}
									onConfirmationFailClick={this.handleConfirmationFailClick}
									onEditDotFormClick={this.handleDotFormClick}
									onSaveConfirmationClick={this.handleSaveConfirmationClick}
									onInspectionDateChangeHandler={this.onInspectionDateChangeHandler}									
                                />
                            }
							
							{this.state.step === this.step.result_confirmation_fail_options &&                                 
                                <DotInspectionResultConfirmationFailOptions
									isSaving={this.props.isSaving}	
									onYesClick={this.handleConfirmationFailYesClick}
									onNoClick={this.handleConfirmationFailNoClick}									
                                />
                            }


                            {this.state.step === this.step.form &&                                 
                                <DotInspectionForm
									formData={this.props.formData}
									formValidationMessages={this.props.formValidationMessages}
									setFormData={this.props.setFormData}
									componentDetails={this.props.componentDetails}
									isSaving={this.props.isSaving}
									userLocale={this.props.userLocale}
									poCreationDate={this.props.poCreationDate}
									onComponentDetailsChanged={this.props.setComponentDetails}
									onAllOkClick={this.handleAllOkClick}
                                    onFormSaveClick={this.handleFormSaveClick}
									onInspectionDateChangeHandler={this.onInspectionDateChangeHandler}
                                />
							}
							
							{this.state.step === this.step.form_fail_options &&                                 
                                <DotInspectionFormFailOptions
									isSaving={this.props.isSaving}
									onYesClick={this.handleFormFailYesClick}
									onNoClick={this.handleFormFailNoClick}
                                />
                            }
                        
                        </div>
                    </Modal.Body>
                </Modal>                
            </div>
        );
    }
}

function mapToStateProps(state, props) {    
	
    return {        
		showWindow: state.appState.getIn(['uiData', 'dotInspection', 'showWindow']),
		errorMessage: state.appState.getIn(['uiData', 'shared', 'errorMessage']),
		dotInspectionErrorMessage: state.appState.getIn(['uiData', 'dotInspection', 'errorMessage']),
        poNumber: state.appState.getIn(['serverData', 'close', 'purchaseOrder', 'id']),
        formData: state.appState.getIn(['uiData', 'dotInspection', 'formData']) || Map(),
		formValidationMessages: state.appState.getIn(['uiData', 'dotInspection', 'formValidationMessages']) || Map(),
		componentDetails: state.appState.getIn(['uiData', 'dotInspection', 'componentDetails']) || Map(),
		shouldSkipFailOptions: state.appState.getIn(['uiData', 'dotInspection', 'shouldSkipFailOptions']) || false,
		isSaving: state.appState.getIn(['uiData', 'dotInspection', 'isSaving']) || false,
		userLocale: state.appState.getIn(['uiData', 'shared', 'selectedLocale']),
		poCreationDate: state.appState.getIn(['serverData', 'close', 'purchaseOrder', 'createdDate']),
    }
}

const mapDispatchToProps = (dispatch, props) => {
    return {		
        reset: () => {
            dispatch({ type: constants.DOT_INSPECTION__RESET });
		},
		resetError: () => {
            dispatch({ type: constants.DOT_INSPECTION__RESET_ERROR });
		},
		setDefaultValues: () => {
			dispatch({ type: constants.DOT_INSPECTION__SET_DEFAULT_VALUES });
		},
		cancelClosePo: () => {
			dispatch({ type: constants.CLOSE_PO_CANCELLED });
		},
        closeWindow: () => {
            dispatch({ type: constants.DOT_INSPECTION__DISPLAY, show: false });
        },
        setFormData: (field, value) => {                                    
            dispatch({ type: constants.DOT_INSPECTION__SET_FORM_DATA, field, value });
        },
        setValidationMessage: (field, message) => {                                    
            dispatch({ type: constants.DOT_INSPECTION__SET_VALIDATION_MESSAGE, field, message });
        },
        resetValidationMessages: () => {
            dispatch({ type: constants.DOT_INSPECTION__RESET_VALIDATION_MESSAGES });
        },
        saveConfirmation: () => {			
			dispatch({ type: constants.DOT_INSPECTION__SAVE_CONFIRMATION });			
		},
		skipFailOptions: () => {
			dispatch({ type: constants.DOT_INSPECTION__SKIP_FAIL_OPTIONS });
		},
        saveForm: () => {			
			dispatch({ type: constants.DOT_INSPECTION__SAVE_FORM });			
        },
        navigateToEditPo: (poNumber) => {            
            dispatch({ type: constants.NAVIGATE_TO, path: `/po/edit/${poNumber}` });
		},
		setComponentDetails: (componentId, field, value) => {                                    
            dispatch({ type: constants.DOT_INSPECTION__SET_COMPONENT_DETAILS, componentId, field, value });
		},
		setAllVehicleComponentsOk: () => {                                    
            dispatch({ type: constants.DOT_INSPECTION__SET_ALL_COMPONENT_OK });
		},
        
    }
}

export default connect(mapToStateProps, mapDispatchToProps)(DotInspectionContainer);
